import { Spacer } from 'app/layouts/generic';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Button } from 'app/shared/button';
import { DateTimePicker } from 'app/shared/datepickers';
import { DetailItem } from 'app/shared/info-section/item';
import { SimpleInput } from 'app/shared/input';
import { TextArea } from 'app/shared/input/textarea';
import { MultiSelect } from 'app/shared/select';
import { useSMSService } from 'hooks/messaging/sms';
import { useAudienceService } from 'hooks/users/audience';
import useValidator from 'hooks/validator';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addOneSMSToStore } from 'store/actions/sms';
import { SMSMessageValidator } from './validator';
import { checkSMSLength } from './helper';
import { InfoBanner } from 'app/shared/info-banner';
import { useSenderIDService } from 'hooks/messaging/sender-id';
import { toast } from 'react-toastify';

const message_types = [
  { value: 'campaign', label: 'Campaign' },
  { value: 'transactional', label: 'Transactional' }
];

export const SMSmessageForm = ({ message_data = {}, closeModal = () => {}, onSave = () => {} }) => {
  const dispatch = useDispatch();
  const { errors, form_is_valid, validateField } = useValidator(SMSMessageValidator);
  const { fetchAudiences } = useAudienceService();
  const { createSMS, fetchSMSs, updateSMS } = useSMSService();
  const { fetchSenderIds } = useSenderIDService();

  const [audience_optionss, setAudienceOptions] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [message_length, setMessageLength] = useState(0);
  const [message_pages, setMessagePages] = useState(1);
  const [schedule, setSchedule] = useState('');
  const [sender_id, setSenderId] = useState({});
  const [sender_id_options, setSenderIdOptions] = useState([]);
  const [title, setTitle] = useState('');
  const [type, setType] = useState({});

  useEffect(() => {
    if (!message_data || !message_data.id) {
      clearForm();
    }
  }, [closeModal]);

  useEffect(() => {
    if (!message_data || !Object.keys(message_data).length) return;
    setCode(message_data.code || '');
    setTitle(message_data.title);
    setMessage(message_data.message);
    setSenderId({ value: message_data.sender_id, label: message_data.sender_id });
    setType(message_types.find((option) => option.value === message_data.type) || {});
    if (message_data.type === 'campaign') {
      setSchedule(message_data.schedule.date);
    }

    const { segments } = checkSMSLength(message_data.message);
    setMessagePages(segments);
  }, [message_data]);

  useEffect(() => {
    fetchSenderIds({ query_string: 'status=granted' }).then(({ sender_ids }) => {
      const parsed_sender_ids = (sender_ids || []).map((s) => ({
        label: s.sender_id,
        value: s.sender_id
      }));
      setSenderIdOptions(() => parsed_sender_ids);
    });

    fetchAudiences().then(({ audiences }) => {
      const audience_by_id = audiences.reduce((s, a) => ({ ...s, [a.id]: a }), {});
      setAudienceOptions(() => audiences.map((aud) => ({ value: aud.id, label: aud.name })));
      if (message_data && message_data.type === 'campaign') {
        setAudiences(() =>
          (message_data.audiences || []).map((aud) => ({
            value: aud,
            label: audience_by_id[aud]?.name || '[DELETED]'
          }))
        );
      }
    });
  }, [closeModal]);

  useEffect(() => {
    validateField('code', code, { type: type.value });
    validateField('message', message);
    validateField('sender_id', sender_id.value);
    validateField('title', title);
    validateField('type', type.value);

    validateField('schedule', schedule, { type: type.value });
    validateField('audiences', audiences, { type: type.value });
  }, [code, title, message, schedule, sender_id, audiences, type]);

  const clearForm = () => {
    setCode('');
    setTitle('');
    setMessage('');
    setSenderId({});
    setSchedule('');
    setType({});
    setAudiences([]);
  };

  const check_code_uniqueness = async (code) => {
    if (!code) return true;

    const { messages } = await fetchSMSs({ query_string: `code=${code}` });
    if (messages.length === 0 || messages.length > 1) return false;

    const { id } = messages[0];
    return id !== message_data?.id;
  };

  const handleMessageChange = (val = '') => {
    const { length, segments } = checkSMSLength(val);
    if (length > 160) return;
    setMessagePages(segments);
    setMessageLength(length);
    setMessage(val);
  };

  const submit = async () => {
    const data = {
      code,
      is_dnd: false,
      message,
      title,
      pages: message_pages || 1,
      sender_id: sender_id.value,
      audiences: audiences.map((audience) => audience.value),
      schedule: { date: schedule ? new Date(schedule) : new Date() },
      status: 'queued',
      type: type.value
    };

    const code_exists = await check_code_uniqueness(code);
    if (type.value === 'transactional' && code_exists) {
      toast.error('The provided code is already in use. Please choose a different code.');
      return;
    }

    setLoading(true);
    const { message: result } = message_data.id
      ? await updateSMS(message_data.id, { data })
      : await createSMS({ data });

    setLoading(false);

    if (!result) return;
    if (message_data.id) result.id = message_data.id;
    dispatch(addOneSMSToStore({ ...message_data, ...data, ...result }));
    onSave(result);
    clearForm();
    closeModal();
  };

  return (
    <>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Title">
            <SimpleInput value={title} onInput={setTitle} error={errors.title} />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={2}>
        <GridColumn>
          <DetailItem title="Sender ID">
            <MultiSelect
              value={sender_id}
              onChange={setSenderId}
              options={sender_id_options}
              error={errors.sender_id}
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Type">
            <MultiSelect
              value={type}
              onChange={setType}
              options={message_types}
              error={errors.type}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      {type.value === 'transactional' && (
        <GridRow num_of_columns={1}>
          <GridColumn>
            <DetailItem title="Code">
              <SimpleInput value={code} onInput={setCode} error={errors.code} />
            </DetailItem>
          </GridColumn>
        </GridRow>
      )}
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Message">
            <TextArea
              value={message}
              onInput={handleMessageChange}
              error={errors.message}
              rows={5}
              show_counter
              character_count={message_length}
              character_limit={160}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      {type.value === 'campaign' && (
        <GridRow num_of_columns={2}>
          <GridColumn>
            <DetailItem title="Audiences">
              <MultiSelect
                value={audiences}
                onChange={setAudiences}
                options={audience_optionss}
                error={errors.audiences}
                isMulti
              />
            </DetailItem>
          </GridColumn>
          <GridColumn>
            <DetailItem title="Schedule">
              <DateTimePicker
                showTime
                value={schedule}
                onChange={setSchedule}
                error={errors.schedule}
              />
            </DetailItem>
          </GridColumn>
        </GridRow>
      )}
      <Spacer multiple={6} />
      {message_pages > 1 && (
        <>
          <InfoBanner
            type="error"
            message={`Special text detected. Your message will be ${message_pages} pages of SMS.`}
          />
          <Spacer multiple={4} />
        </>
      )}
      <GridRow num_of_columns={2}>
        <GridColumn>
          <Button text="Clear" type="secondary" onClick={clearForm} />
        </GridColumn>
        <GridColumn>
          <Button text="Save" disabled={!form_is_valid || loading} onClick={submit} />
        </GridColumn>
      </GridRow>
    </>
  );
};
